import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  Container, Paper, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Select, MenuItem,
  FormControl, InputLabel, Button, Badge, IconButton, Alert,
  TextField, InputAdornment, Grid, Card, CardContent, Stack,
  useMediaQuery, useTheme, Fade, Grow, Skeleton
} from '@mui/material';
import { 
  Refresh, ArrowUpward, ArrowDownward, NotInterested,
  Notifications, CheckCircle, LocalShipping, DoneAll, VolumeUp, VolumeOff,
  Search as SearchIcon, Clear as ClearIcon, FilterList, SortByAlpha
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import notification from "../assets/notification.mp3";

const AdminOrdersPage = ({keywordsData, language}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortNewest, setSortNewest] = useState(true);
  const [stats, setStats] = useState(null);
  const [pendingAlert, setPendingAlert] = useState(0);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  // Search states
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [customerNameSearch, setCustomerNameSearch] = useState('');
  const [searchTimer, setSearchTimer] = useState(null);
  
  const newFirstTxt = language === 'ar' ? 'الأحدث أولا' : 'Newest First';
  const oldFirstTxt = language === 'ar' ? 'الأقدم أولا' : 'Oldest First';
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSound_pinding');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const audioRef = useRef(null);
  const lastNotificationTimeRef = useRef(0);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(notification);
    audioRef.current.load();
    
    const enableAudio = () => {
      if (audioRef.current) {
        audioRef.current.play().catch(() => {}).then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        });
      }
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
    
    document.addEventListener('click', enableAudio);
    document.addEventListener('touchstart', enableAudio);
    
    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
    };
  }, []);

  const playBeepSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.5);
      
      return true;
    } catch (error) {
      console.log('Web Audio API not supported or blocked');
      return false;
    }
  };

  const playNotificationSound = () => {
    if (!soundEnabled) return;
    
    const now = Date.now();
    if (now - lastNotificationTimeRef.current < 2000) {
      return;
    }
    
    lastNotificationTimeRef.current = now;
    
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play()
        .catch(() => playBeepSound());
    } else {
      playBeepSound();
    }
  };

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('notificationSound_pinding', JSON.stringify(newValue));
      return newValue;
    });
  };

  const statusColors = {
    canceled: 'error',
    pending: 'warning',
    processing: 'info',
    shipped: 'primary',
    delivered: 'success'
  };

  const statusTxt = {
    canceled: language === 'ar' ? 'ملغي' : 'Canceled',
    pending: language === 'ar' ? 'معلق' : 'Pending',
    processing: language === 'ar' ? 'قيد التحضير' : 'Preparation',
    shipped: language === 'ar' ? 'قيد التوصيل' : 'On road',
    delivered: language === 'ar' ? 'مكتمل' : 'Complete'
  };

  const statusIcons = {
    pending: <Notifications color="warning" sx={{ fontSize: { xs: 16, sm: 20 } }} />,
    processing: <CheckCircle color="info" sx={{ fontSize: { xs: 16, sm: 20 } }} />,
    shipped: <LocalShipping color="primary" sx={{ fontSize: { xs: 16, sm: 20 } }} />,
    delivered: <DoneAll color="success" sx={{ fontSize: { xs: 16, sm: 20 } }} />,
    canceled: <NotInterested color="error" sx={{ fontSize: { xs: 16, sm: 20 } }} />
  };

  // Debounced search function
  const handleSearch = (searchOrderId, searchCustomerName) => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    
    const timer = setTimeout(() => {
      fetchOrders(searchOrderId, searchCustomerName);
    }, 500);
    
    setSearchTimer(timer);
  };

  const handleOrderIdSearchChange = (e) => {
    const value = e.target.value;
    setOrderIdSearch(value);
    handleSearch(value, customerNameSearch);
  };

  const handleCustomerNameSearchChange = (e) => {
    const value = e.target.value;
    setCustomerNameSearch(value);
    handleSearch(orderIdSearch, value);
  };

  const clearOrderIdSearch = () => {
    setOrderIdSearch('');
    fetchOrders('', customerNameSearch);
  };

  const clearCustomerNameSearch = () => {
    setCustomerNameSearch('');
    fetchOrders(orderIdSearch, '');
  };

  const clearAllSearches = () => {
    setOrderIdSearch('');
    setCustomerNameSearch('');
    fetchOrders('', '');
  };

  const fetchOrders = async (searchOrderId = orderIdSearch, searchCustomerName = customerNameSearch) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      params.append('newest', sortNewest);
      
      if (searchOrderId) params.append('orderId', searchOrderId);
      if (searchCustomerName) params.append('customerName', searchCustomerName);
      
      const response = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(response.data);
      setError(null);
      
      const statsRes = await api.get('/admin/orders/stats');
      setStats(statsRes.data);
      setPendingAlert(statsRes.data.pendingCount);
        
      if (statsRes.data.pendingCount > 0) {
        playNotificationSound();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load orders');
      console.error('Orders fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'casher') {
      fetchOrders();
      const interval = setInterval(() => fetchOrders(), 120000);
      return () => clearInterval(interval);
    }
  }, [user, filter, sortNewest]);

  if (user?.role !== 'admin' && user?.role !== 'casher') {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Access denied. Admin or Casher privileges required.
        </Alert>
      </Container>
    );
  }

  // Mobile order card component
  const OrderCard = ({ order }) => (
    <Grow in>
      <Card sx={{ 
        mb: 2, 
        bgcolor: '#0D0D0D', 
        border: '1px solid #2D2D2D',
        borderRadius: 2,
        '&:hover': { bgcolor: '#1A1A1A' }
      }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 600 }}>
                #{order.id}
              </Typography>
              <Chip
                label={statusTxt[order.status]}
                color={statusColors[order.status]}
                icon={statusIcons[order.status]}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
            
            <Box>
              <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                {language === 'ar' ? 'العميل' : 'Customer'}
              </Typography>
              <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                {order.user?.username || 'Guest'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {order.user?.email || 'No email'}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                {language === 'ar' ? 'المنتجات' : 'Products'}
              </Typography>
              {order.items?.slice(0, 2).map((item) => (
                <Typography key={item.id} sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
                  {item.quantity}x {language === 'ar' ? item.product.name : item.product.name_en}
                </Typography>
              ))}
              {order.items?.length > 2 && (
                <Typography sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                  +{order.items.length - 2} {language === 'ar' ? 'أكثر' : 'more'}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>
                {order.total_amount} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
              </Typography>
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {new Date(order.created_at).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={order.payment_method === 'cash_on_delivery' ? (language === 'ar' ? 'نقدي' : 'Cash') : 
                       order.payment_method === 'stripe' ? 'Stripe' : 'Visa'}
                size="small"
                sx={{ 
                  bgcolor: order.payment_method === 'cash_on_delivery' ? '#2D2D2D' : '#FFD700',
                  color: order.payment_method === 'cash_on_delivery' ? '#FFFFFF' : '#000000'
                }}
              />
              <Chip
                label={order.table_id !== 0 ? (language === 'ar' ? `طاولة ${order.table_id}` : `Table ${order.table_id}`) : 
                        (language === 'ar' ? 'خارجي' : 'Take away')}
                size="small"
                sx={{ 
                  bgcolor: order.table_id !== 0 ? '#2D2D2D' : '#FFD700',
                  color: order.table_id !== 0 ? '#FFFFFF' : '#000000'
                }}
              />
              {order?.pax_no > 0 && (
                <Chip
                  label={language === 'ar' ? `${order.pax_no} شخص` : `${order.pax_no} Person`}
                  size="small"
                  sx={{ bgcolor: '#ff0000', color: '#FFFFFF' }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Select
                  disabled={order.status === 'delivered' || order.status === 'canceled'}
                  value={order.status}
                  onChange={(e) => updateStatus(order.id, e.target.value)}
                  sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}
                >
                  <MenuItem value="pending">{language === 'ar' ? 'معلقة' : 'Pending'}</MenuItem>
                  <MenuItem value="processing">{language === 'ar' ? 'قيد التحضير' : 'Preparation'}</MenuItem>
                  <MenuItem value="shipped">{language === 'ar' ? 'قيد التوصيل' : 'On road'}</MenuItem>
                  <MenuItem sx={{ color: '#3ad00d'}} value="delivered">{language === 'ar' ? 'مكتملة' : 'Complete'}</MenuItem>
                  <MenuItem sx={{ color: '#eb3107'}} value="canceled">{language === 'ar' ? 'ملغية' : 'Canceled'}</MenuItem>
                </Select>
              </FormControl>
              <IconButton
                sx={{ color: '#FFD700', border: '1px solid #FFD700', borderRadius: 1 }}
                onClick={() => navigate(user.role === 'admin' ? `/admin/orders/${order.id}` : `/casher/orders/${order.id}`)}
              >
                {language === 'ar' ? <ArrowBackIcon /> : <ArrowForward />}
              </IconButton>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grow>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 }, mb: 10 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3 
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
          {language === 'ar' ? 'إدارة الطلبات' : 'Orders Management'}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <IconButton 
            onClick={toggleSound} 
            sx={{ 
              color: soundEnabled ? '#FFD700' : '#B3B3B3',
              bgcolor: 'rgba(255,255,255,0.05)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            {soundEnabled ? <VolumeUp /> : <VolumeOff />}
          </IconButton>
          
          <Button 
            variant="contained" 
            startIcon={<Refresh />}
            onClick={() => fetchOrders()}
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </Button>
          
          <Badge badgeContent={pendingAlert} color="error" overlap="circular">
            <Chip 
              icon={<Notifications />}
              label={isMobile ? '' : (language === 'ar' ? 'طلبات معلقة' : 'Pending orders')}
              color="warning"
              variant={filter === 'pending' ? 'filled' : 'outlined'}
              onClick={() => setFilter('pending')}
              clickable
              sx={{ 
                pl: isMobile ? 1 : 2,
                pr: isMobile ? 1 : 2,
                '& .MuiChip-icon': { mr: isMobile ? 0 : 0.5 }
              }}
            />
          </Badge>
        </Box>
      </Box>

      {/* Search Section */}
      <Paper sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: 3, 
        bgcolor: '#0D0D0D', 
        border: '1px solid #2D2D2D',
        borderRadius: 2
      }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label={language === 'ar' ? 'البحث برقم الطلب' : 'Search by Order ID'}
              value={orderIdSearch}
              onChange={handleOrderIdSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
                endAdornment: orderIdSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearOrderIdSearch}>
                      <ClearIcon sx={{ color: '#B3B3B3', fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { color: '#FFFFFF' }
              }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
          </Grid>
          
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label={language === 'ar' ? 'البحث باسم العميل' : 'Search by Customer Name'}
              value={customerNameSearch}
              onChange={handleCustomerNameSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
                endAdornment: customerNameSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearCustomerNameSearch}>
                      <ClearIcon sx={{ color: '#B3B3B3', fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { color: '#FFFFFF' }
              }}
              InputLabelProps={{ sx: { color: '#B3B3B3' } }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            {(orderIdSearch || customerNameSearch) && (
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAllSearches}
                sx={{ 
                  borderColor: '#FFD700', 
                  color: '#FFD700',
                  borderRadius: 2,
                  '&:hover': { borderColor: '#FFD700', bgcolor: 'rgba(255,215,0,0.1)' }
                }}
              >
                {language === 'ar' ? 'مسح البحث' : 'Clear Search'}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Filters Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2, 
        mb: 3,
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
          <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'فلترة' : 'Filter'}</InputLabel>
          <Select
            value={filter}
            label={language === 'ar' ? 'فلترة' : 'Filter'}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ color: '#FFFFFF', borderRadius: 2 }}
          >
            <MenuItem value="all">{language === 'ar' ? 'الكل' : 'All'}</MenuItem>
            <MenuItem value="pending">{language === 'ar' ? 'معلقة' : 'Pending'}</MenuItem>
            <MenuItem value="processing">{language === 'ar' ? 'قيد التحضير' : 'Preparation'}</MenuItem>
            <MenuItem value="shipped">{language === 'ar' ? 'قيد التوصيل' : 'On road'}</MenuItem>
            <MenuItem sx={{ color: '#3ad00d'}} value="delivered">{language === 'ar' ? 'مكتملة' : 'Complete'}</MenuItem>
            <MenuItem sx={{ color: '#eb3107'}} value="canceled">{language === 'ar' ? 'ملغية' : 'Canceled'}</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          endIcon={sortNewest ? <ArrowDownward /> : <ArrowUpward />}
          onClick={() => setSortNewest(!sortNewest)}
          sx={{ 
            borderColor: '#FFD700', 
            color: '#FFD700',
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': { borderColor: '#FFD700', bgcolor: 'rgba(255,215,0,0.1)' }
          }}
        >
          {isMobile ? (
            <SortByAlpha />
          ) : (
            `${language === 'ar' ? 'ترتيب حسب' : 'Sort by'}: ${sortNewest ? newFirstTxt : oldFirstTxt}`
          )}
        </Button>

        {stats && !isMobile && (
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
            {stats.stats.map((stat) => (
              <Chip
                key={stat.status}
                icon={statusIcons[stat.status]}
                label={`${statusTxt[stat.status]}: ${stat.count}`}
                color={statusColors[stat.status]}
                variant={filter === stat.status ? 'filled' : 'outlined'}
                onClick={() => setFilter(stat.status)}
                clickable
                size={isTablet ? "small" : "medium"}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Mobile Stats Chips */}
      {stats && isMobile && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          {stats.stats.map((stat) => (
            <Chip
              key={stat.status}
              icon={statusIcons[stat.status]}
              label={`${statusTxt[stat.status]}: ${stat.count}`}
              color={statusColors[stat.status]}
              variant={filter === stat.status ? 'filled' : 'outlined'}
              onClick={() => setFilter(stat.status)}
              clickable
              size="small"
            />
          ))}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Orders Display - Mobile Cards or Desktop Table */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={isMobile ? 200 : 80} sx={{ bgcolor: '#1A1A1A' }} />
          ))}
        </Box>
      ) : isMobile ? (
        // Mobile View - Card Layout
        <Box>
          {orders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: 2 }}>
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا يوجد طلبات جديدة' : 'No new orders yet'}
              </Typography>
            </Paper>
          ) : (
            orders.map((order) => <OrderCard key={order.id} order={order} />)
          )}
        </Box>
      ) : (
        // Desktop View - Table Layout
        <TableContainer component={Paper} elevation={3} sx={{ 
          bgcolor: '#0D0D0D', 
          border: '1px solid #2D2D2D',
          borderRadius: 2,
          overflowX: 'auto'
        }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1A1A1A' }}>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'رقم الطلب' : 'Order ID'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'العميل' : 'Customer'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'المنتجات' : 'Products'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'الإجمالي' : 'Total'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'التاريخ' : 'Date'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'طريقة الدفع' : 'payment method'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'التوصيل' : 'Deliver'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'عدد الأشخاص' : 'Pax No'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ color: '#B3B3B3', py: 4 }}>
                    {language === 'ar' ? 'لا يوجد طلبات جديدة' : 'No new orders yet'}
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} sx={{ '&:hover': { bgcolor: '#1A1A1A' } }}>
                    <TableCell sx={{ color: '#FFFFFF', fontWeight: 500 }}>#{order.id}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography sx={{ color: '#FFFFFF' }}>{order.user?.username || 'Guest'}</Typography>
                        <Typography variant="body2" sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                          {order.user?.email || 'No email'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {order.items?.slice(0, 2).map((item) => (
                        <Typography key={item.id} sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
                          {item.quantity}x {language === 'ar' ? item.product.name : item.product.name_en}
                        </Typography>
                      ))}
                      {order.items?.length > 2 && (
                        <Typography sx={{ color: '#B3B3B3', fontSize: '0.75rem' }}>
                          +{order.items.length - 2} {language === 'ar' ? 'أكثر' : 'more'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#FFD700', fontWeight: 600 }}>{order.total_amount} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}</TableCell>
                    <TableCell sx={{ color: '#B3B3B3' }}>
                      <div>{new Date(order.created_at).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}</div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                        {new Date(order.created_at).toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusTxt[order.status]}
                        color={statusColors[order.status]}
                        icon={statusIcons[order.status]}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.payment_method === 'cash_on_delivery' ? (language === 'ar' ? 'نقدي' : 'Cash') : 
                               order.payment_method === 'stripe' ? 'Stripe' : 'Visa'}
                        size="small"
                        sx={{ 
                          bgcolor: order.payment_method === 'cash_on_delivery' ? '#2D2D2D' : '#FFD700',
                          color: order.payment_method === 'cash_on_delivery' ? '#FFFFFF' : '#000000'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.table_id !== 0 ? (language === 'ar' ? `طاولة ${order.table_id}` : `Table ${order.table_id}`) : 
                                (language === 'ar' ? 'خارجي' : 'Take away')}
                        size="small"
                        sx={{ 
                          bgcolor: order.table_id !== 0 ? '#2D2D2D' : '#FFD700',
                          color: order.table_id !== 0 ? '#FFFFFF' : '#000000'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {order?.pax_no > 0 && (
                        <Chip
                          label={language === 'ar' ? `${order.pax_no} شخص` : `${order.pax_no} Person`}
                          size="small"
                          sx={{ bgcolor: '#ff0000', color: '#FFFFFF' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          disabled={order.status === 'delivered' || order.status === 'canceled'}
                          value={order.status}
                          onChange={(e) => updateStatus(order.id, e.target.value)}
                          sx={{ color: '#FFFFFF' }}
                        >
                          <MenuItem value="pending">{language === 'ar' ? 'معلقة' : 'Pending'}</MenuItem>
                          <MenuItem value="processing">{language === 'ar' ? 'قيد التحضير' : 'Preparation'}</MenuItem>
                          <MenuItem value="shipped">{language === 'ar' ? 'قيد التوصيل' : 'On road'}</MenuItem>
                          <MenuItem sx={{ color: '#3ad00d'}} value="delivered">{language === 'ar' ? 'مكتملة' : 'Complete'}</MenuItem>
                          <MenuItem sx={{ color: '#eb3107'}} value="canceled">{language === 'ar' ? 'ملغية' : 'Canceled'}</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        sx={{ color: '#FFD700' }}
                        onClick={() => navigate(user.role === 'admin' ? `/admin/orders/${order.id}` : `/casher/orders/${order.id}`)}
                      >
                        {language === 'ar' ? <ArrowBackIcon /> : <ArrowForward />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Search results count */}
      {(orderIdSearch || customerNameSearch) && (
        <Fade in>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography sx={{ color: '#B3B3B3', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {language === 'ar' 
                ? `تم العثور على ${orders.length} نتيجة للبحث`
                : `Found ${orders.length} results for your search`}
            </Typography>
          </Box>
        </Fade>
      )}
    </Container>
  );
};

export default AdminOrdersPage;